import getTransactions from 'api/v1/transactions/getTransactions';
import type { ActivationStatusResponse } from 'api/v2/accountable-banking/activation';
import useQuery from 'hooks/react-query/useQuery';
import { PostAccountStepId } from '../../../components/pages/Banks/BankingSignUp/ActivationFlow/constants/stepIds';
import useAccountableBanks from '../useAccountableBanks';
import { useGetActivationStatus } from '../useActivationStatus';
import { useIsInActivationFlowExperiment } from '../useIsInActivationFlowExperiment';

// ─── Pure decision logic (exported for testing) ──────────────────────

export const MIN_TRANSACTIONS_FOR_AB_ACTIVATION = 1;

export interface OverlayDecisionInput {
  isLoading: boolean;
  isError: boolean;
  isInActivationFlowExperiment: boolean;
  mainAccountIban: string | undefined;
  furthestStep: string | null;
  /** undefined = query not yet resolved; number = totalCount */
  transactionCount: number | undefined;
}

/**
 * Pure function containing the overlay visibility decision logic.
 * Extracted from the hook for testability.
 *
 * 1. Guard: loading / error / not in experiment / no IBAN / completed → false
 * 2. Has ongoing activation progress → true (regardless of tx count)
 * 3. Otherwise → show only if transactions <= MIN_TRANSACTIONS_FOR_AB_ACTIVATION
 */
export function computeShouldShowOverlay(input: OverlayDecisionInput): boolean {
  const {
    isLoading,
    isError,
    isInActivationFlowExperiment,
    mainAccountIban,
    furthestStep,
    transactionCount,
  } = input;

  // Guard: bail out early
  if (
    isLoading ||
    isError ||
    !isInActivationFlowExperiment ||
    !mainAccountIban ||
    furthestStep === PostAccountStepId.ReadyToGo
  ) {
    return false;
  }

  // User has ongoing activation (progress exists and not completed)
  const hasOngoingABActivation = furthestStep !== null;

  if (hasOngoingABActivation) {
    return true;
  }

  // New user: show only if tx data is loaded and count is low
  if (transactionCount !== undefined) {
    return transactionCount <= MIN_TRANSACTIONS_FOR_AB_ACTIVATION;
  }

  return false;
}

/**
 * Hook to determine if the activation overlay should be displayed
 *
 * Shows overlay when user is
 * 1. in group B;
 * AND
 * 2. activation is not completed;
 * AND either:
 * 3.
 * a) User has existing activation progress (let them finish regardless of tx count), OR
 * b) Main AB account has <= 1 transaction (new user)
 *
 * Optimization: Transactions query is skipped when user has ongoing activation progress,
 * since the result won't affect the decision (matches mobile).
 *
 * @returns An object containing:
 * - shouldShow: boolean - whether the activation overlay should be shown
 * - activationStatus: ActivationStatusResponse | undefined - the current activation status data
 * - isLoading: boolean - whether the activation status is currently loading
 * - isError: boolean - whether there was an error fetching the activation status
 */
export const useShouldShowActivationOverlay = (): {
  shouldShow: boolean;
  activationStatus: ActivationStatusResponse | undefined;
  isLoading: boolean;
  isError: boolean;
} => {
  const {
    data: activationStatus,
    isLoading: isLoadingActivation,
    isError: isErrorActivation,
  } = useGetActivationStatus();
  const isInActivationFlowExperiment = useIsInActivationFlowExperiment();
  const { mainAccountableBank, isLoading: isLoadingBank } =
    useAccountableBanks();

  const mainAccountIban = mainAccountableBank?.bankAccountReference?.IBAN;

  const furthestStep = activationStatus?.activationFurthestStepReached ?? null;

  // User has ongoing activation (progress exists and not completed)
  const hasOngoingABActivation =
    furthestStep !== null && furthestStep !== PostAccountStepId.ReadyToGo;

  // Only check transactions when there's no ongoing activation (optimization: skip query otherwise)
  const shouldCheckTransactions =
    !!mainAccountIban &&
    isInActivationFlowExperiment &&
    !hasOngoingABActivation;

  // Fetch transaction count for main AB account (minimal payload: 1 item to get totalCount)
  const {
    data: transactionsData,
    isLoading: isLoadingTransactions,
    isError: isErrorTransactions,
  } = useQuery(
    ['ab-main-account-transaction-count', mainAccountIban],
    () =>
      getTransactions({
        accountNumbers: mainAccountIban,
        page: 1,
        perPage: 1,
      }),
    {
      enabled: shouldCheckTransactions,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  );

  const transactionCount = transactionsData?.paging?.totalCount ?? 0;

  const isLoading =
    isLoadingActivation ||
    isLoadingBank ||
    (shouldCheckTransactions && isLoadingTransactions);
  const isError =
    isErrorActivation || (shouldCheckTransactions && isErrorTransactions);

  // Delegate to pure function (same logic, testable)
  const shouldShow = computeShouldShowOverlay({
    isLoading,
    isError,
    isInActivationFlowExperiment,
    mainAccountIban,
    furthestStep,
    transactionCount:
      transactionsData !== undefined ? transactionCount : undefined,
  });

  return { shouldShow, activationStatus, isLoading, isError };
};
